import type { WorldRoomDirectory, WorldRoomEntry } from "@cleo/contracts";
import type { ConvexClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";

import { NEEDS_THE_MAC_KIND } from "../webHost";
import { worldWords } from "./cloudConversations";
import {
  findWorkroom,
  frontOpeningBoard,
  workroomEntrySessionId,
  type WorkroomDoorOutcome,
} from "./workroomDoor";
import { readWorldRoomEntry } from "./worldSessionClient";
import type { WorkroomSessionIntent } from "./useWorkroomSessionBirth";

/**
 * cleo-web (webHost.ts): a Workroom's session born from the browser, with no Mac.
 *
 * On the Desk, + on a Workroom card opens a session of that room through the Desk server's session door (workroomDoor.ts
 * → worldSessionClient.ts openWorldSessionEntry), and the session then runs on an engine the Mac drives. The browser host
 * has no Mac server and no engine; what it has is the World's own door for a conversation kept in the World
 * (cleoConversations:openOrCreate, the one Pocket calls and WebHostLanding's New calls), which since PR #2245 takes the
 * room: the World opens the session under that room through the same door the Desk's + uses, records the conversation,
 * and answers with the session. The page then reads the entry back from the World (boards:readRooms through
 * webHostWorldDoors.ts), accepts it into the rail, fronts the room's opening board and navigates, exactly as the Desk
 * door does after its own birth; the session's leaf shows the conversation (CloudConversationPane), where Cleo answers
 * through the World (cleoTurnSend.send).
 *
 * What still needs the Mac says so in one line: a Workroom the World does not hold yet is born with its room by the
 * Desk's room door (a World door the browser has no person door for).
 */

type OpenInRoomArgs = {
  worldKey: string;
  clientKey: string;
  room: { roomId: string; label: string };
  /** A Desk folder conversation's Mac (steps 5-7 design §2.3): its folder tools use this Mac. */
  macTools?: { mac: string };
};

const openInRoomRef = makeFunctionReference<
  "action",
  OpenInRoomArgs,
  {
    readonly roomId: string;
    readonly sessionId: string;
    readonly conversationId: string;
    readonly created: boolean;
  }
>("cleoConversations:openOrCreate");

export type OpenWorkroomConversation = (
  client: ConvexClient,
  args: OpenInRoomArgs,
) => Promise<{ readonly roomId: string; readonly sessionId: string; readonly created: boolean }>;

export const openWorkroomConversation: OpenWorkroomConversation = (client, args) =>
  client.action(openInRoomRef, args);

/** The one line the browser says for a room it cannot make. Pure. */
export const makingRoomNeedsTheMac = (title: string): string =>
  `Making the ${title} room needs the Cleo Mac app; open it there. Nothing was started.`;

export async function birthWorkroomSessionInBrowser(input: {
  readonly worldKey: string;
  /** A rendering snapshot; the World's doors decide. */
  readonly index: WorldRoomDirectory;
  readonly workroomKey: string;
  readonly title: string;
  readonly intent: WorkroomSessionIntent;
  readonly client: ConvexClient | null;
  readonly clientKey: string;
  readonly acceptEntryReadback: (entry: WorldRoomEntry) => boolean | void;
  readonly navigate: (target: { readonly sessionId: string }) => void | Promise<void>;
  readonly open?: OpenWorkroomConversation;
  readonly readEntry?: typeof readWorldRoomEntry;
  readonly frontBoard?: (
    worldKey: string,
    sessionId: string,
    workroomKey: string,
  ) => void | Promise<void>;
}): Promise<WorkroomDoorOutcome> {
  const existing = findWorkroom(input.index, input.workroomKey);
  if (existing === undefined)
    return { ok: false, kind: NEEDS_THE_MAC_KIND, reason: makingRoomNeedsTheMac(input.title) };
  if (input.client === null)
    return {
      ok: false,
      kind: "not_signed_in",
      reason: "This window is not signed in to the World.",
    };
  const roomId = existing.roomId;
  const finish = async (sessionId: string, created: boolean): Promise<WorkroomDoorOutcome> => {
    const read = await (input.readEntry ?? readWorldRoomEntry)({
      worldKey: input.worldKey,
      roomId,
      sessionId,
    });
    if (!read.ok) return read;
    const entry = read.entry;
    if (
      entry.worldKey !== input.worldKey ||
      entry.room.roomId !== roomId ||
      entry.room.kind !== "workroom" ||
      entry.room.workroomKey !== input.workroomKey ||
      entry.session.sessionId !== sessionId ||
      entry.session.parentId !== roomId
    )
      return {
        ok: false,
        kind: "invalid_room",
        reason: "The requested workroom session did not read back exactly.",
      };
    if (input.acceptEntryReadback(entry) === false)
      return {
        ok: false,
        kind: "context_changed",
        reason: "The active World or sign-in changed while opening the room.",
      };
    const current = () => input.acceptEntryReadback(entry) !== false;
    const fronted = input.frontBoard
      ? await input.frontBoard(input.worldKey, sessionId, input.workroomKey)
      : await frontOpeningBoard(input.worldKey, sessionId, input.workroomKey, current);
    const unchanged = current();
    if (fronted === false || !unchanged)
      return {
        ok: false,
        kind: unchanged ? "opening_unavailable" : "context_changed",
        reason: unchanged
          ? "The Workroom's opening content is unavailable. Try opening it again."
          : "The active World or sign-in changed while opening the room.",
      };
    await input.navigate({ sessionId });
    return { ok: true, roomId, sessionId, created };
  };
  if (input.intent === "resume") {
    // The room where it stands: its latest root session as the index holds it (the Desk asks the Mac for the latest
    // root; the browser has the World's index, read page by page). A room with no session yet starts one.
    const latest = workroomEntrySessionId(input.index, roomId);
    if (latest !== null) return finish(latest, false);
  }
  let born: Awaited<ReturnType<OpenWorkroomConversation>>;
  try {
    born = await (input.open ?? openWorkroomConversation)(input.client, {
      worldKey: input.worldKey,
      clientKey: input.clientKey,
      room: { roomId, label: input.title },
    });
  } catch (error) {
    return { ok: false, kind: "upstream_refusal", reason: worldWords(error) };
  }
  if (born.roomId !== roomId)
    return {
      ok: false,
      kind: "invalid_room",
      reason: "The World opened the conversation in another room.",
    };
  return finish(born.sessionId, born.created);
}
